// context/UserContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import appConfig from '../config';

const UserContext = createContext();

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const UserProvider = ({ children, userData: initialUserData = null }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Восстановление сессии при загрузке
    useEffect(() => {
        const restoreSession = () => {
            setIsLoading(true);

            try {
                // 1. Если есть данные от родителя
                if (initialUserData) {
                    setUser(initialUserData);
                    setIsLoading(false);
                    return;
                }

                // 2. Пробуем восстановить из localStorage
                const token = localStorage.getItem('token');
                const savedUser = localStorage.getItem('user');

                if (token && savedUser) {
                    const parsedUser = JSON.parse(savedUser);

                    // Проверяем, не устарели ли данные (опционально)
                    const expiresAt = localStorage.getItem('token_expires');
                    if (expiresAt && Date.now() > parseInt(expiresAt)) {
                        // Токен истек - чистим все
                        logout();
                    } else {
                        // Восстанавливаем сессию
                        setUser(parsedUser);
                        appConfig.setToken(token);
                        console.log('Сессия восстановлена:', parsedUser);
                    }
                }
            } catch (error) {
                console.error('Ошибка восстановления сессии:', error);
                // При ошибке чистим localStorage
                localStorage.removeItem('user');
                localStorage.removeItem('token');
                localStorage.removeItem('token_expires');
            } finally {
                setIsLoading(false);
            }
        };

        restoreSession();
    }, [initialUserData]);

    const login = (userData, token, expiresInDays = 30) => {
        // Сохраняем токен
        localStorage.setItem('token', token);
        appConfig.setToken(token);

        // Сохраняем пользователя как JSON
        localStorage.setItem('user', JSON.stringify(userData));

        // Сохраняем дату истечения (опционально)
        const expiresAt = Date.now() + (expiresInDays * 24 * 60 * 60 * 1000);
        localStorage.setItem('token_expires', expiresAt.toString());

        // Сохраняем в state
        setUser(userData);
    };

    const logout = () => {
        // Удаляем все из localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('token_expires');

        // Чистим состояние
        appConfig.setToken(null);
        setUser(null);
    };

    const updateUser = (userData) => {
        // Обновляем в state
        setUser(prevUser => {
            const updatedUser = {
                ...prevUser,
                ...userData
            };

            // Обновляем в localStorage
            localStorage.setItem('user', JSON.stringify(updatedUser));

            return updatedUser;
        });
    };

    const value = {
        user,
        login,
        logout,
        updateUser,
        isLoading,
        isAuthenticated: !!user && !!localStorage.getItem('token')
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};